import { useEffect, useState } from "react";

import { trpc } from "../trpc";

export interface JurisdictionRow {
  code: string;
  name: string;
}

interface UseJurisdictionsReturn {
  jurisdictions: JurisdictionRow[] | null;
  /** Friendly full name of the vertical — used in the appbar title. */
  verticalName: string;
  /** Compact shortName — used in the lede prefix ("CDL rules vary..."). */
  verticalShortName: string;
  defaultSelected: string | null;
  error: string | null;
}

// Owns the state-picker fetch + "Federal only" chip injection.
// Federal stays as an explicit option (last) with a friendlier label
// than the raw FEDERAL code. California is the default-selected state
// (per the brief — alphabetical Alaska is the wrong launch anchor).
export function useJurisdictions(
  verticalSlug: string | null,
): UseJurisdictionsReturn {
  const [jurisdictions, setJurisdictions] = useState<
    JurisdictionRow[] | null
  >(null);
  const [verticalName, setVerticalName] = useState<string>("");
  const [verticalShortName, setVerticalShortName] = useState<string>("");
  const [defaultSelected, setDefaultSelected] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!verticalSlug) {
      setError("Missing vertical. Go back and pick one.");
      return;
    }
    void (async () => {
      try {
        const v = await trpc.vertical.bySlug.query({ slug: verticalSlug });
        if (!v) {
          setError("Vertical not found.");
          return;
        }
        setVerticalName(v.name);
        setVerticalShortName(v.shortName);
        const stateChips: JurisdictionRow[] = v.jurisdictions
          .filter((j) => j.code !== "FEDERAL")
          .map((j) => ({ code: j.code, name: j.name }));
        const federalChip = v.jurisdictions.find((j) => j.code === "FEDERAL");
        const list: JurisdictionRow[] = federalChip
          ? [...stateChips, { code: "FEDERAL", name: "Federal only" }]
          : stateChips;
        const ca = list.find((j) => j.code === "CA");
        setJurisdictions(list);
        setDefaultSelected(ca?.code ?? list[0]?.code ?? null);
      } catch (err) {
        setError(err instanceof Error ? err.message : "failed to load");
      }
    })();
  }, [verticalSlug]);

  return {
    jurisdictions,
    verticalName,
    verticalShortName,
    defaultSelected,
    error,
  };
}
